import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useAuth } from '@qwickapps/auth-client';
import { Button, Text } from '@qwickapps/react-framework';
import { useEffect, useState } from 'react';
import UpgradeNotice from '../UpgradeNotice';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'pending' | 'suspended';
  lastActive?: string;
}

export default function UsersSettings() {
  const { session } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    loadUsers();
  }, [session]);

  const loadUsers = async () => {
    if (!session?.accessToken) return;

    setLoading(true);
    try {
      // TODO: Implement API call to fetch organization users
      // Placeholder data
      setUsers([
        {
          id: '1',
          email: session.user?.email || '',
          name: session.user?.user_metadata?.full_name || 'You',
          role: 'owner',
          status: 'active',
          lastActive: 'Just now',
        },
      ]);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return { backgroundColor: 'var(--theme-error)', color: 'var(--theme-on-error)' };
      case 'admin':
        return { backgroundColor: 'var(--theme-warning)', color: 'var(--theme-on-warning)' };
      default:
        return {};
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return { backgroundColor: 'var(--theme-success)', color: 'var(--theme-on-success)' };
      case 'pending':
        return { backgroundColor: 'var(--theme-warning)', color: 'var(--theme-on-warning)' };
      case 'suspended':
        return { backgroundColor: 'var(--theme-error)', color: 'var(--theme-on-error)' };
      default:
        return {};
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Text variant="h6" gutterBottom customColor="var(--theme-accent-dark)">
            Team Members
          </Text>
          <Text variant="body2" customColor="var(--theme-text)">
            Manage user access and permissions for your organization
          </Text>
        </Box>
        <Button
          icon={<PersonAddIcon />}
          backgroundColor='var(--theme-primary)'
          sx={{ color: 'var(--theme-text)' }}
          onClick={() => console.log('Invite user')}
          disabled
        >
          Invite User
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <UpgradeNotice
        message="User management is available on Team and Enterprise plans. Upgrade to add team members and collaborate."
      />

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip label={user.role} size="small" sx={getRoleColor(user.role)} />
                </TableCell>
                <TableCell>
                  <Chip label={user.status} size="small" sx={getStatusColor(user.status)} />
                </TableCell>
                <TableCell>{user.lastActive}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, user)}
                    disabled={user.role === 'owner'}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>Change Role</MenuItem>
        <MenuItem onClick={handleMenuClose}>Suspend User</MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: 'var(--theme-error)' }}>
          Remove User
        </MenuItem>
      </Menu>
    </Box>
  );
}
